// SPDX-License-Identifier: AGPL-3.0-or-later

import {useAnimatedImageUrl} from '@app/features/app/hooks/useAnimatedImageUrl';
import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import {GifIndicator} from '@app/features/channel/components/embeds/media/GifIndicator';
import {
	getUserMenuAvatarUrl,
	getUserMenuBannerUrl,
	UserImageMenuItems,
} from '@app/features/ui/action_menu/items/UserImageMenuItems';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {getAppRemScale} from '@app/features/ui/utils/AppZoomUtils';
import userProfileModalStyles from '@app/features/user/components/modals/UserProfileModal.module.css';
import {
	isKeyboardContextMenuTrigger,
	type ProfileMediaHeaderProps,
} from '@app/features/user/components/modals/user_profile_modal/UserProfileModalShared';
import {
	getProfileAvatarDecorationBounds,
	getProfileAvatarFrameBounds,
	getProfileAvatarLeft,
	PROFILE_MODAL_BANNER_AVATAR_CUTOUT,
	PROFILE_MODAL_GEOMETRY,
	PROFILE_MODAL_GEOMETRY_STYLE,
} from '@app/features/user/constants/UserProfileSurfaceGeometry';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const OPEN_BANNER_OPTIONS_DESCRIPTOR = msg({
	message: 'Open banner options',
	comment: 'Button or menu action label in the user profile modal. Keep it concise. Keep the tone plain and specific.',
});
const OPEN_AVATAR_OPTIONS_DESCRIPTOR = msg({
	message: 'Open avatar options',
	comment: 'Button or menu action label in the user profile modal. Keep it concise.',
});

const PROFILE_MODAL_DEFAULT_SCALE = 1.0666667;

function getProfileModalScale(bannerWidth: number | null): number {
	return (
		(bannerWidth ?? PROFILE_MODAL_BANNER_AVATAR_CUTOUT.viewBoxWidthPx * PROFILE_MODAL_DEFAULT_SCALE) /
		PROFILE_MODAL_BANNER_AVATAR_CUTOUT.viewBoxWidthPx
	);
}

export const ProfileMediaHeader: React.FC<ProfileMediaHeaderProps> = observer(
	({
		user,
		profile,
		profileContext,
		previewOverrides,
		bannerColor,
		bannerUrl,
		hoverBannerUrl,
		avatarUrl,
		hoverAvatarUrl,
		renderActionButtons,
		identity,
	}) => {
		const {i18n} = useLingui();
		const {
			hoverRef: bannerHoverRef,
			imageUrl: activeBannerUrl,
			showGifIndicator,
		} = useAnimatedImageUrl({
			staticUrl: bannerUrl,
			animatedUrl: hoverBannerUrl,
			kind: 'gif',
		});
		const [bannerElement, setBannerElement] = useState<HTMLDivElement | null>(null);
		const [bannerWidth, setBannerWidth] = useState<number | null>(null);
		const setMeasuredBannerElement = useCallback((node: HTMLDivElement | null) => {
			setBannerElement(node);
			if (!node) return;
			setBannerWidth(node.getBoundingClientRect().width);
		}, []);
		const mergedBannerRef = useMergeRefs<HTMLDivElement>([bannerHoverRef, setMeasuredBannerElement]);
		useEffect(() => {
			if (!bannerElement) return;
			const ownerWindow = bannerElement.ownerDocument.defaultView ?? window;
			const measure = () => setBannerWidth(bannerElement.getBoundingClientRect().width);
			measure();
			if (typeof ownerWindow.ResizeObserver === 'undefined') return;
			const resizeObserver = new ownerWindow.ResizeObserver((entries) => {
				const entry = entries[0];
				setBannerWidth(entry?.contentRect.width ?? bannerElement.getBoundingClientRect().width);
			});
			resizeObserver.observe(bannerElement);
			return () => resizeObserver.disconnect();
		}, [bannerElement]);
		const avatarScale = getProfileModalScale(bannerWidth);
		const avatarSize = PROFILE_MODAL_GEOMETRY.avatarSizePx * avatarScale;
		const avatarBorder = PROFILE_MODAL_GEOMETRY.avatarBorderPx * avatarScale;
		const bounds = identity ? getProfileAvatarDecorationBounds(user, avatarSize, avatarBorder) : null;
		const wideUltra = Boolean(identity && user.avatarUltra && !MobileLayout.enabled);
		const avatarLeft =
			bounds && !wideUltra
				? getProfileAvatarLeft({
						decorationLeft: bounds.left,
						size: avatarSize,
						border: avatarBorder,
						edgePadding: PROFILE_MODAL_GEOMETRY.avatarLeftPx * avatarScale,
						containerWidth: bannerWidth ?? PROFILE_MODAL_BANNER_AVATAR_CUTOUT.viewBoxWidthPx * avatarScale,
					})
				: PROFILE_MODAL_GEOMETRY.avatarLeftPx * avatarScale;
		const avatarTop = bounds
			? Math.max(
					PROFILE_MODAL_GEOMETRY.avatarTopPx * avatarScale,
					-PROFILE_MODAL_GEOMETRY.bannerViewBoxHeightPx * avatarScale - avatarBorder - bounds.top,
				)
			: PROFILE_MODAL_GEOMETRY.avatarTopPx * avatarScale;
		// Micup: on the wide page the ultra draws over the name block, so only the avatar and its frame push it aside.
		const clearance = wideUltra ? getProfileAvatarFrameBounds(user, avatarSize, avatarBorder) : bounds;
		const headerStyle = clearance
			? ({
					'--profile-avatar-clearance-width': `calc(${wideUltra ? avatarLeft + avatarBorder + clearance.right : Math.max(avatarLeft + avatarBorder + clearance.right, clearance.right - clearance.left)}px - 1rem * var(--profile-modal-scale))`,
					'--profile-avatar-clearance-height': `max(0px, calc(${avatarTop + avatarBorder + clearance.bottom}px - 1rem * var(--profile-modal-scale)))`,
				} as React.CSSProperties)
			: undefined;
		const avatarContainerStyle = useMemo<React.CSSProperties>(
			() => ({
				top: avatarTop,
				left: avatarLeft,
				borderWidth: PROFILE_MODAL_GEOMETRY.avatarBorderPx * avatarScale,
			}),
			[avatarScale, avatarTop, avatarLeft],
		);
		const menuUrlOptions = {user, profile, profileContext, previewOverrides};
		const hasAvatarMenu = Boolean(getUserMenuAvatarUrl(menuUrlOptions));
		const hasBannerMenu = Boolean(getUserMenuBannerUrl(menuUrlOptions));
		const renderAvatarMenu = useCallback(
			({onClose}: {onClose: () => void}) => (
				<UserImageMenuItems
					user={user}
					profile={profile}
					profileContext={profileContext}
					previewOverrides={previewOverrides}
					onClose={onClose}
					variant="avatar"
					data-flx="user.user-profile-modal.render-avatar-menu.user-image-menu-items"
				/>
			),
			[user, profile, profileContext, previewOverrides],
		);
		const renderBannerMenu = useCallback(
			({onClose}: {onClose: () => void}) => (
				<UserImageMenuItems
					user={user}
					profile={profile}
					profileContext={profileContext}
					previewOverrides={previewOverrides}
					onClose={onClose}
					variant="banner"
					data-flx="user.user-profile-modal.render-banner-menu.user-image-menu-items"
				/>
			),
			[user, profile, profileContext, previewOverrides],
		);
		const handleImageContextMenu = useCallback(
			(
				event: React.MouseEvent<HTMLElement>,
				hasMenu: boolean,
				renderMenu: (props: {onClose: () => void}) => React.ReactNode,
			) => {
				if (!hasMenu) return;
				ContextMenuCommands.openFromEvent(event, renderMenu);
			},
			[],
		);
		const handleImageKeyDown = useCallback(
			(
				event: React.KeyboardEvent<HTMLElement>,
				hasMenu: boolean,
				renderMenu: (props: {onClose: () => void}) => React.ReactNode,
			) => {
				if (!hasMenu || !isKeyboardContextMenuTrigger(event)) return;
				event.preventDefault();
				event.stopPropagation();
				ContextMenuCommands.openForElement(event.currentTarget, renderMenu);
			},
			[],
		);
		// Micup: no accent block and no avatar cutout; a banner image fades into the profile.
		const bannerContent = (
			<div
				className={userProfileModalStyles.bannerImage}
				style={activeBannerUrl ? {backgroundImage: `url(${activeBannerUrl})`} : undefined}
				data-flx="user.user-profile-modal.profile-media-header.div"
			/>
		);
		const avatarContent = (
			<StatusAwareAvatar
				size={avatarSize / getAppRemScale()}
				decorationSurface="profile"
				user={user}
				avatarUrl={avatarUrl}
				hoverAvatarUrl={hoverAvatarUrl}
				forceAnimate
				forceAnimateIgnoringSettings
				data-flx="user.user-profile-modal.profile-media-header.status-aware-avatar"
			/>
		);
		return (
			<header
				className={activeBannerUrl ? userProfileModalStyles.withBanner : undefined}
				style={{...PROFILE_MODAL_GEOMETRY_STYLE, '--profile-card-accent': bannerColor} as React.CSSProperties}
				data-flx="user.user-profile-modal.profile-media-header.header"
			>
				<FocusRing enabled={hasBannerMenu} data-flx="user.user-profile-modal.profile-media-header.focus-ring">
					{/* biome-ignore lint/a11y/noStaticElementInteractions: the banner only takes role="button"/tabIndex when an image menu exists, and the element stays mounted either way so the banner image is never refetched. */}
					{/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-label is set only alongside role="button". */}
					<div
						ref={mergedBannerRef}
						className={userProfileModalStyles.bannerContainer}
						onContextMenu={
							hasBannerMenu ? (event) => handleImageContextMenu(event, hasBannerMenu, renderBannerMenu) : undefined
						}
						onKeyDown={
							hasBannerMenu ? (event) => handleImageKeyDown(event, hasBannerMenu, renderBannerMenu) : undefined
						}
						role={hasBannerMenu ? 'button' : undefined}
						tabIndex={hasBannerMenu ? 0 : undefined}
						aria-label={hasBannerMenu ? i18n._(OPEN_BANNER_OPTIONS_DESCRIPTOR) : undefined}
						data-flx="user.user-profile-modal.profile-media-header.button.image-key-down"
					>
						{bannerContent}
						{showGifIndicator && <GifIndicator data-flx="user.user-profile-modal.profile-media-header.gif-indicator" />}
					</div>
				</FocusRing>
				<div
					className={`${userProfileModalStyles.headerContainer} ${identity ? userProfileModalStyles.headerContainerIdentity : ''}`}
					style={headerStyle}
					data-flx="user.user-profile-modal.profile-media-header.div--3"
				>
					<FocusRing
						enabled={hasAvatarMenu}
						ringClassName={userProfileModalStyles.avatarFocusRing}
						data-flx="user.user-profile-modal.profile-media-header.focus-ring--2"
					>
						{/* biome-ignore lint/a11y/noStaticElementInteractions: the avatar frame only takes role="button"/tabIndex when an image menu exists, and the element stays mounted either way so the avatar is never refetched. */}
						{/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-label is set only alongside role="button". */}
						<div
							className={userProfileModalStyles.avatarContainer}
							style={avatarContainerStyle}
							onContextMenu={
								hasAvatarMenu ? (event) => handleImageContextMenu(event, hasAvatarMenu, renderAvatarMenu) : undefined
							}
							onKeyDown={
								hasAvatarMenu ? (event) => handleImageKeyDown(event, hasAvatarMenu, renderAvatarMenu) : undefined
							}
							role={hasAvatarMenu ? 'button' : undefined}
							tabIndex={hasAvatarMenu ? 0 : undefined}
							aria-label={hasAvatarMenu ? i18n._(OPEN_AVATAR_OPTIONS_DESCRIPTOR) : undefined}
							data-flx="user.user-profile-modal.profile-media-header.button.image-key-down--2"
						>
							{avatarContent}
						</div>
					</FocusRing>
					{identity}
					<div
						className={userProfileModalStyles.actionButtonsContainer}
						data-flx="user.user-profile-modal.profile-media-header.div--5"
					>
						{renderActionButtons()}
					</div>
				</div>
			</header>
		);
	},
);
